/**
 * Hook para debounce - Otimização de Performance
 * 
 * Este hook implementa debounce para evitar chamadas excessivas
 * durante digitação em campos de busca e filtros.
 * 
 * @author Equipa Tamoaki
 * @version 1.0.0
 * @date 2025-08-03
 */

import { useState, useEffect } from 'react';

export function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

/**
 * Hook para debounce de função
 * @param callback - Função a ser debounced
 * @param delay - Delay em milissegundos (padrão: 300ms)
 * @returns Função debounced
 */
export function useDebouncedCallback<T extends (...args: any[]) => any>(
  callback: T,
  delay: number = 300
): T {
  const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout | null>(null);

  const debouncedCallback = ((...args: Parameters<T>) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    const newTimeoutId = setTimeout(() => {
      callback(...args);
    }, delay);

    setTimeoutId(newTimeoutId);
  }) as T;

  useEffect(() => {
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [timeoutId]);

  return debouncedCallback;
}

/**
 * Hook para debounce de busca com cache
 * @param searchFunction - Função de busca
 * @param delay - Delay em milissegundos (padrão: 300ms)
 * @returns Função de busca debounced com cache
 */
export function useDebouncedSearch<T>(
  searchFunction: (query: string) => Promise<T>,
  delay: number = 300
) {
  const [cache, setCache] = useState<Map<string, T>>(new Map());
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const debouncedSearch = useDebouncedCallback(async (query: string) => {
    if (!query.trim()) {
      setIsLoading(false);
      return;
    }

    // Verificar cache primeiro
    if (cache.has(query)) {
      return cache.get(query);
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await searchFunction(query);
      
      // Adicionar ao cache
      setCache(prev => new Map(prev).set(query, result));
      
      return result;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro na busca');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, delay);

  const clearCache = () => {
    setCache(new Map());
  };

  return {
    search: debouncedSearch,
    isLoading,
    error,
    clearCache,
  };
}

/**
 * Hook para debounce de filtros com otimização
 * @param filterFunction - Função de filtro
 * @param delay - Delay em milissegundos (padrão: 500ms)
 * @returns Função de filtro debounced
 */
export function useDebouncedFilter<T>(
  filterFunction: (filters: any) => Promise<T[]>,
  delay: number = 500
) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastFilters, setLastFilters] = useState<any>(null);

  const debouncedFilter = useDebouncedCallback(async (filters: any) => {
    // Evitar filtros duplicados
    if (JSON.stringify(filters) === JSON.stringify(lastFilters)) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await filterFunction(filters);
      setLastFilters(filters);
      return result;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro no filtro');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, delay);

  return {
    filter: debouncedFilter,
    isLoading,
    error,
  };
}

export default useDebounce; 